import { Navigate } from "react-router-dom";

/**
 * Iter32 — smarter admin gate.
 * We don't just check that a token exists — we also decode it (best-effort,
 * no signature verification client-side) and ensure:
 *  - it hasn't expired (`exp` claim > now)
 *  - the user's role/scope claim is 'admin'
 * If either fails we wipe the local token and bounce to /admin/login. Any
 * decode error is treated the same as an expired token.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function isValidAdminToken(token) {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  // Expiry — `exp` is seconds since epoch per RFC 7519.
  if (payload.exp && payload.exp * 1000 < Date.now()) return false;
  // Role gate — accept either an explicit "admin" flag or role/scope claim.
  const role = payload.role || payload.scope || (payload.is_admin ? "admin" : null);
  return role === "admin";
}

export default function AdminPrivateRoute({ children }) {
  const token = localStorage.getItem("kobolog_admin_token");
  if (!isValidAdminToken(token)) {
    // Clean up any stale/expired token so we don't retry with it.
    try { localStorage.removeItem("kobolog_admin_token"); } catch (_) { /* storage disabled */ }
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}
